import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Tag, Check, X, Loader2 } from "lucide-react";

/**
 * Inline voucher input + live preview that calls POST /api/vouchers/validate
 * Used inside the booking form on VehicleDetail.jsx.
 *
 * Auto-revalidates ONLY when the booking inputs (dates/times/locations/addons)
 * change *after* a code has been applied — every other render is a no-op.
 *
 * Props
 *  - code: current voucher_code string in parent state
 *  - onCodeChange(code)
 *  - onPreviewChange(preview | null)   // { code, type, value, discount, ... }
 *  - context: { vehicle_id, pickup_date, return_date, pickup_time, return_time,
 *               pickup_location, return_location, addons }
 *  - disabled: bool
 */
export default function VoucherInput({
  code, onCodeChange, onPreviewChange, context, disabled,
}) {
  const [draft, setDraft] = useState(code || "");
  const [status, setStatus] = useState("idle"); // idle | loading | applied | error
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  // The last (code + context) hash we successfully validated. Used to:
  //  (a) skip re-fetching for unchanged inputs
  //  (b) defeat the infinite-loop trap where parent re-render mutates
  //      callback identities and triggers the effect again.
  const lastSignatureRef = useRef("");

  // Stable primitive deps — never include the `context` object literal or
  // parent callbacks, since both change identity on every parent render.
  const ctxKey = `${context?.pickup_date || ""}|${context?.return_date || ""}|${context?.pickup_time || ""}|${context?.return_time || ""}|${context?.pickup_location || ""}|${context?.return_location || ""}|${(context?.addons || []).slice().sort().join(",")}`;

  // Reset the widget when the parent clears the applied code (e.g. user
  // navigates back to a fresh booking form).
  useEffect(() => {
    if (!code) {
      setDraft("");
      setStatus("idle");
      setError("");
      setPreview(null);
      lastSignatureRef.current = "";
    }
  }, [code]);

  const callValidate = async (rawCode) => {
    const norm = rawCode.trim().toUpperCase();
    const sig = `${norm}|${ctxKey}`;
    // Idempotency guard — prevents re-render storms.
    if (!norm || sig === lastSignatureRef.current) return;

    if (!context?.vehicle_id || !context?.pickup_date || !context?.return_date) {
      setStatus("error");
      setError("Pick your dates first, then apply the code.");
      onPreviewChange(null);
      return;
    }

    setStatus("loading");
    setError("");
    try {
      const { data } = await api.post("/vouchers/validate", { code: norm, ...context });
      lastSignatureRef.current = sig;
      setDraft(data.code);
      setPreview(data);
      setStatus("applied");
      onCodeChange(data.code);
      onPreviewChange(data);
    } catch (err) {
      lastSignatureRef.current = "";
      setPreview(null);
      setStatus("error");
      setError(formatApiError(err.response?.data?.detail) || err.message);
      onCodeChange("");
      onPreviewChange(null);
    }
  };

  // Manual trigger: blur / Enter / button click.
  const validate = () => callValidate(draft);

  // Auto-revalidate when booking context changes AFTER a code is applied.
  // Depending only on primitives prevents the parent-render → new-context →
  // re-fire infinite loop reported by iter5 testing.
  useEffect(() => {
    if (status !== "applied" || !code) return;
    const sig = `${code}|${ctxKey}`;
    if (sig === lastSignatureRef.current) return;
    callValidate(code);
  }, [ctxKey]);

  const clear = () => {
    setDraft("");
    setStatus("idle");
    setError("");
    setPreview(null);
    onCodeChange("");
    onPreviewChange(null);
    lastSignatureRef.current = "";
  };

  return (
    <div data-testid="voucher-widget" className="mt-6">
      <p className="overline mb-3 flex items-center gap-2">
        <Tag size={11} /> Promo code
      </p>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value.toUpperCase());
              if (status === "applied" || status === "error") {
                setStatus("idle");
                setError("");
                setPreview(null);
                lastSignatureRef.current = "";
                onCodeChange("");
                onPreviewChange(null);
              }
            }}
            onBlur={validate}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                validate();
              }
            }}
            placeholder="ENTER CODE"
            disabled={disabled}
            className="editorial-input w-full font-mono tracking-widest uppercase pr-10 disabled:opacity-50"
            data-testid="voucher-code-input"
            maxLength={32}
          />
          {status === "loading" && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-neutral-400" data-testid="voucher-loading" />
          )}
          {status === "applied" && (
            <Check size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600" />
          )}
          {status === "error" && (
            <X size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
          )}
        </div>
        <button
          type="button"
          onClick={status === "applied" ? clear : validate}
          disabled={disabled || (!draft.trim() && status !== "applied")}
          className="px-5 text-xs uppercase tracking-wider border border-black/15 hover:border-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={status === "applied" ? "voucher-remove-button" : "voucher-apply-button"}
        >
          {status === "applied" ? "Remove" : "Apply"}
        </button>
      </div>

      {status === "error" && error && (
        <p className="text-red-600 text-xs mt-2" data-testid="voucher-error">{error}</p>
      )}

      {status === "applied" && preview && (
        <div className="mt-3 p-3 border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-sm" data-testid="voucher-preview">
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono font-semibold tracking-widest">{preview.code}</span>
            <span className="text-[10px] uppercase tracking-widest text-emerald-700">
              {preview.type === "percent" ? `${preview.value}% off` : `${Number(preview.value).toFixed(0)} off`}
            </span>
          </div>
          {preview.description && (
            <p className="text-[11px] mt-1 text-emerald-800/80">{preview.description}</p>
          )}
          <p className="text-sm mt-2 tabular-nums">
            <strong>–€{Number(preview.discount).toFixed(2)}</strong> applied to subtotal
            {" "}<span className="text-[11px] text-emerald-800/70">(pre-tax)</span>
          </p>
        </div>
      )}
    </div>
  );
}
